import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { isBefore, parseISO } from "date-fns";
import { Clock, CheckSquare, MessageSquare, Paperclip } from "lucide-react";
import {
  Card,
  Box,
  Typography,
  Chip,
  Avatar,
  AvatarGroup,
} from "@mui/material";
import { red, amber, blue, grey, orange } from "@mui/material/colors";
import { MOCK_USERS } from "../utils";
import { useTheme } from "@emotion/react";

const getPriorityChipColor = (priority) => {
  switch (priority) {
    case "Critical":
      return { bgcolor: red[100], color: red[900] };
    case "High":
      return { bgcolor: orange[100], color: orange[900] };
    case "Medium":
      return { bgcolor: blue[100], color: blue[900] };
    case "Low":
    default:
      return { bgcolor: grey[100], color: grey[800] };
  }
};

export const TaskCard = ({ task, onClick, isOverlay }) => {
  const isOverdue =
    isBefore(parseISO(task.dueDate), new Date()) && task.status !== "Done";
  const theme = useTheme();
  // Calculate checklist progress for preview
  const totalChecklist = task.checklist?.length || 0;
  const completedChecklist =
    task.checklist?.filter((c) => c.isCompleted).length || 0;

  const priorityColors = getPriorityChipColor(task.priority);

  return (
    <Card
      onClick={() => onClick?.(task)}
      sx={{
        p: 2,
        borderRadius: 2,
        cursor: isOverlay ? "grabbing" : "grab",
        border: "1px solid",
        borderColor: isOverdue ? "error.light" : "divider",
        bgcolor: isOverdue
          ? theme.palette.mode === "dark"
            ? red[200]
            : red[50]
          : "background.paper",
        boxShadow: isOverlay ? 10 : 1,
        transform: isOverlay ? "scale(1.05) rotate(2deg)" : "none",
        transition: "all 0.2s",
        "&:hover": {
          boxShadow: 3,
          borderColor: "primary.main",
        },
        "&:active": {
          cursor: "grabbing",
        },
        position: "relative",
      }}
      elevation={0}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={1}
      >
        <Chip
          label={task.priority}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.7rem",
            fontWeight: "bold",
            ...priorityColors,
          }}
        />
        {isOverdue && <Clock size={16} color={red[500]} />}
      </Box>

      <Typography
        variant="subtitle2"
        fontWeight="bold"
        color="text.primary"
        gutterBottom
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          lineHeight: 1.3,
          mb: 1.5,
        }}
      >
        {task.title}
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <AvatarGroup
          max={3}
          sx={{ "& .MuiAvatar-root": { width: 24, height: 24, fontSize: 12 } }}
        >
          {task.assigneeIds.map((id) => {
            const user = MOCK_USERS.find((u) => u.id === id);
            if (!user) return null;
            return <Avatar key={id} src={user.avatar} alt={user.name} />;
          })}
        </AvatarGroup>

        <Box display="flex" gap={1.5} color="text.secondary">
          {totalChecklist > 0 && (
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              title="Checklist items"
            >
              <CheckSquare size={14} />
              <Typography variant="caption" fontWeight="medium">
                {completedChecklist}/{totalChecklist}
              </Typography>
            </Box>
          )}
          {task.comments?.length > 0 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <MessageSquare size={14} />
              <Typography variant="caption" fontWeight="medium">
                {task.comments.length}
              </Typography>
            </Box>
          )}
          {task.attachments?.length > 0 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Paperclip size={14} />
              <Typography variant="caption" fontWeight="medium">
                {task.attachments.length}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export const SortableTaskItem = ({ task, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1, // Hide original item completely while dragging
    marginBottom: 12, // mb-3
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TaskCard task={task} onClick={onClick} />
    </div>
  );
};
